import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import FocalPointSelector from '../../../components/FocalPointSelector';
import {
  Calendar,
  Save,
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Settings,
  FileText,
  CreditCard,
  QrCode,
  Eye,
  ChevronUp,
  ChevronDown,
  Copy
} from 'lucide-react';
import axios from 'axios';
import { goBack } from '../../../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Tipos de campo disponibles
const TIPOS_CAMPO = [
  { value: 'text', label: 'Texto corto', icon: '📝' },
  { value: 'textarea', label: 'Texto largo', icon: '📄' },
  { value: 'number', label: 'Número', icon: '🔢' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'phone', label: 'Teléfono', icon: '📱' },
  { value: 'date', label: 'Fecha', icon: '📅' },
  { value: 'select', label: 'Selección única', icon: '⚪' },
  { value: 'multiselect', label: 'Selección múltiple', icon: '☑️' },
  { value: 'checkbox', label: 'Casilla (Sí/No)', icon: '✅' },
  { value: 'dropdown', label: 'Desplegable', icon: '📋' },
  { value: 'file', label: 'Archivo/Imagen', icon: '📎' },
];

// Campos disponibles para prellenado automático
const CAMPOS_PRELLENADO = [
  { value: 'none', label: 'Sin prellenar' },
  { value: 'nombre', label: 'Nombre del usuario' },
  { value: 'apellido', label: 'Apellido del usuario' },
  { value: 'email', label: 'Email del usuario' },
  { value: 'telefono', label: 'Teléfono del usuario' },
  { value: 'matricula', label: 'N° Matrícula' },
  { value: 'dni', label: 'DNI/Documento' },
  { value: 'distrito', label: 'Distrito' },
];

export default function AdminEventoFormPage() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const isEditing = eventoId && eventoId !== 'nuevo';
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Estado del evento
  const [evento, setEvento] = useState({
    nombre: '',
    slug: '',
    descripcion: '',
    descripcion_corta: '',
    fecha_evento: '',
    hora_evento: '',
    lugar: '',
    imagen_portada: '',
    imagen_focal_point: { x: 50, y: 50 },
    media_promocional: '',
    media_promocional_tipo: '',
    estado: 'borrador',
    modalidad_admision: 'confirmacion_email',
    requiere_login: true,
    verificar_deuda: false,
    cupo_maximo: '',
    inscripciones_abiertas: true,
    habilitar_lista_espera: true,
    multiple_entradas: false,
    max_entradas_por_usuario: 1,
    tiene_beneficio_sello: false,
    campos_formulario: [],
    configuracion_pago: {
      tipo: 'gratis',
      precio: 0,
      precio_con_deuda: 0,
      permitir_con_deuda: true,
      datos_transferencia: ''
    },
    configuracion_ticket: {
      template_base64: '',
      campos_posicion: {},
      ancho: 800,
      alto: 400
    }
  });

  useEffect(() => {
    if (isEditing) {
      fetchEvento();
    }
  }, [eventoId]);

  const fetchEvento = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/eventos/admin/${eventoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvento({
        ...res.data,
        cupo_maximo: res.data.cupo_maximo || '',
        inscripciones_abiertas: res.data.inscripciones_abiertas !== false,
        habilitar_lista_espera: res.data.habilitar_lista_espera !== false,
        multiple_entradas: res.data.multiple_entradas || false,
        max_entradas_por_usuario: res.data.max_entradas_por_usuario || 1,
        tiene_beneficio_sello: res.data.tiene_beneficio_sello || false,
        media_promocional: res.data.media_promocional || '',
        media_promocional_tipo: res.data.media_promocional_tipo || '',
        imagen_focal_point: res.data.imagen_focal_point || { x: 50, y: 50 },
        configuracion_pago: res.data.configuracion_pago || {
          tipo: 'gratis',
          precio: 0,
          precio_con_deuda: 0,
          permitir_con_deuda: true,
          datos_transferencia: ''
        },
        configuracion_ticket: res.data.configuracion_ticket || {
          template_base64: '',
          campos_posicion: {},
          ancho: 800,
          alto: 400
        }
      });
    } catch (err) {
      console.error('Error cargando evento:', err);
      alert('Error al cargar el evento');
      navigate('/admin/eventos');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!evento.nombre.trim()) {
      alert('El nombre del evento es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...evento,
        cupo_maximo: evento.cupo_maximo ? parseInt(evento.cupo_maximo) : null,
        configuracion_pago: {
          ...evento.configuracion_pago,
          precio: parseFloat(evento.configuracion_pago.precio) || 0,
          precio_con_deuda: parseFloat(evento.configuracion_pago.precio_con_deuda) || 0
        }
      };

      if (isEditing) {
        await axios.put(`${API_URL}/eventos/admin/${eventoId}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const res = await axios.post(`${API_URL}/eventos/admin/crear`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        navigate(`/admin/eventos/${res.data.id}/editar`, { replace: true });
      }
      alert('Evento guardado exitosamente');
    } catch (err) {
      console.error('Error guardando evento:', err);
      alert(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ===== FUNCIONES DE CAMPOS DE FORMULARIO =====
  
  const addCampo = (tipo) => {
    const nuevoCampo = {
      id: `campo_${Date.now()}`,
      tipo,
      label: TIPOS_CAMPO.find(t => t.value === tipo)?.label || 'Nuevo campo',
      placeholder: '',
      required: false,
      options: tipo === 'select' || tipo === 'multiselect' || tipo === 'dropdown' ? ['Opción 1', 'Opción 2'] : [],
      seccion: '',
      orden: evento.campos_formulario.length,
      prellenar_con: ''
    };
    
    setEvento(prev => ({
      ...prev,
      campos_formulario: [...prev.campos_formulario, nuevoCampo]
    }));
  };

  const updateCampo = (index, field, value) => {
    setEvento(prev => ({
      ...prev,
      campos_formulario: prev.campos_formulario.map((c, i) => 
        i === index ? { ...c, [field]: value } : c
      )
    }));
  };

  const removeCampo = (index) => {
    setEvento(prev => ({
      ...prev,
      campos_formulario: prev.campos_formulario.filter((_, i) => i !== index)
    }));
  };

  const moveCampo = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= evento.campos_formulario.length) return;
    
    const campos = [...evento.campos_formulario];
    [campos[index], campos[newIndex]] = [campos[newIndex], campos[index]];
    
    setEvento(prev => ({
      ...prev,
      campos_formulario: campos.map((c, i) => ({ ...c, orden: i }))
    }));
  };

  const duplicarCampo = (index) => {
    const campo = evento.campos_formulario[index];
    const nuevoCampo = {
      ...campo,
      id: `campo_${Date.now()}`,
      label: `${campo.label} (copia)`,
      orden: evento.campos_formulario.length
    };
    
    setEvento(prev => ({
      ...prev,
      campos_formulario: [...prev.campos_formulario, nuevoCampo]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button variant="ghost" onClick={() => goBack()}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {isEditing ? 'Editar Evento' : 'Nuevo Evento'}
            </h1>
            {evento.estado && (
              <Badge className={
                evento.estado === 'publicado' ? 'bg-green-100 text-green-700' :
                evento.estado === 'archivado' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-700'
              }>
                {evento.estado}
              </Badge>
            )}
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 w-full sm:w-auto">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 scroll-x-safe w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="general" className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
            <Settings className="w-4 h-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="formulario" className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
            <FileText className="w-4 h-4" />
            Formulario
            <Badge variant="secondary" className="ml-1">{evento.campos_formulario.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="pago" className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
            <CreditCard className="w-4 h-4" />
            Pago
          </TabsTrigger>
          <TabsTrigger value="ticket" className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
            <QrCode className="w-4 h-4" />
            Ticket/QR
          </TabsTrigger>
        </TabsList>

        {/* Tab: General */}
        <TabsContent value="general">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Información básica</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="nombre">Nombre del evento *</Label>
                  <Input
                    id="nombre"
                    value={evento.nombre}
                    onChange={(e) => setEvento(prev => ({ ...prev, nombre: e.target.value }))}
                    placeholder="Ej: Cena de Fin de Año 2025"
                  />
                </div>

                <div>
                  <Label htmlFor="slug">Link personalizado <span className="text-gray-400">(opcional)</span></Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500 whitespace-nowrap">.../eventos/</span>
                    <Input
                      id="slug"
                      value={evento.slug || ''}
                      onChange={(e) => {
                        const v = e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^a-z0-9-]/g, '');
                        setEvento(prev => ({ ...prev, slug: v }));
                      }}
                      placeholder={isEditing ? evento.slug : "cena-fin-de-anio-2026"}
                      data-testid="evento-slug-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {isEditing
                      ? 'Cambiá solo si querés un nuevo link. El link anterior dejará de funcionar.'
                      : 'Si lo dejás vacío se genera del nombre. Solo minúsculas, números y guiones.'}
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="descripcion_corta">Descripción corta</Label>
                  <Input
                    id="descripcion_corta"
                    value={evento.descripcion_corta}
                    onChange={(e) => setEvento(prev => ({ ...prev, descripcion_corta: e.target.value }))}
                    placeholder="Resumen breve para listados"
                  />
                </div>
                
                <div>
                  <Label htmlFor="descripcion">Descripción completa</Label>
                  <Textarea
                    id="descripcion"
                    value={evento.descripcion}
                    onChange={(e) => setEvento(prev => ({ ...prev, descripcion: e.target.value }))}
                    placeholder="Descripción detallada del evento..."
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="fecha_evento">Fecha</Label>
                    <Input
                      id="fecha_evento"
                      type="date"
                      value={evento.fecha_evento}
                      onChange={(e) => setEvento(prev => ({ ...prev, fecha_evento: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="hora_evento">Hora</Label>
                    <Input
                      id="hora_evento"
                      type="time"
                      value={evento.hora_evento}
                      onChange={(e) => setEvento(prev => ({ ...prev, hora_evento: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="cupo_maximo">Cupo máximo (opcional)</Label>
                    <Input
                      id="cupo_maximo"
                      type="number"
                      value={evento.cupo_maximo}
                      onChange={(e) => setEvento(prev => ({ ...prev, cupo_maximo: e.target.value }))}
                      placeholder="Sin límite"
                    />
                  </div>
                </div>

                {/* Controles de inscripciones */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="inscripciones_abiertas" className="cursor-pointer">Inscripciones abiertas</Label>
                      <p className="text-xs text-gray-500">Si está desactivado, no se aceptan nuevas inscripciones</p>
                    </div>
                    <Switch
                      id="inscripciones_abiertas"
                      checked={evento.inscripciones_abiertas}
                      onCheckedChange={(checked) => setEvento(prev => ({ ...prev, inscripciones_abiertas: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="habilitar_lista_espera" className="cursor-pointer">Habilitar lista de espera</Label>
                      <p className="text-xs text-gray-500">Permite anotarse cuando el cupo está lleno o cerrado</p>
                    </div>
                    <Switch
                      id="habilitar_lista_espera"
                      checked={evento.habilitar_lista_espera}
                      onCheckedChange={(checked) => setEvento(prev => ({ ...prev, habilitar_lista_espera: checked }))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="lugar">Lugar</Label>
                  <Input
                    id="lugar"
                    value={evento.lugar}
                    onChange={(e) => setEvento(prev => ({ ...prev, lugar: e.target.value }))}
                    placeholder="Dirección o nombre del lugar"
                  />
                </div>

                <div>
                  <Label>Imagen de portada</Label>
                  <div className="space-y-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setEvento(prev => ({ 
                              ...prev, 
                              imagen_portada: reader.result,
                              imagen_focal_point: { x: 50, y: 50 } // Reset al centro al subir nueva imagen
                            }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <div className="text-xs text-gray-500">O ingresá una URL:</div>
                    <Input
                      id="imagen_portada"
                      value={evento.imagen_portada?.startsWith('data:') ? '' : evento.imagen_portada}
                      onChange={(e) => setEvento(prev => ({ 
                        ...prev, 
                        imagen_portada: e.target.value,
                        imagen_focal_point: { x: 50, y: 50 }
                      }))}
                      placeholder="https://..."
                    />
                  </div>
                  {evento.imagen_portada && (
                    <div className="mt-4 space-y-4">
                      <div className="flex items-start justify-between">
                        <FocalPointSelector
                          imageUrl={evento.imagen_portada}
                          focalPoint={evento.imagen_focal_point || { x: 50, y: 50 }}
                          onChange={(fp) => setEvento(prev => ({ ...prev, imagen_focal_point: fp }))}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setEvento(prev => ({ ...prev, imagen_portada: '', imagen_focal_point: { x: 50, y: 50 } }))}
                        >
                          Quitar imagen
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Media promocional (flyer/video) */}
                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">📸 Media promocional (Flyer/Video)</Label>
                  <p className="text-xs text-gray-500 mb-3">Agregá una imagen o video para promocionar el evento</p>
                  
                  <div className="space-y-2">
                    <Input
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const isVideo = file.type.startsWith('video/');
                            setEvento(prev => ({ 
                              ...prev, 
                              media_promocional: reader.result,
                              media_promocional_tipo: isVideo ? 'video' : 'image'
                            }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <div className="text-xs text-gray-500">O ingresá una URL (imagen o video de YouTube/Vimeo):</div>
                    <Input
                      value={evento.media_promocional?.startsWith('data:') ? '' : evento.media_promocional || ''}
                      onChange={(e) => {
                        const url = e.target.value;
                        // Detectar si es video de YouTube/Vimeo
                        const isVideo = url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');
                        setEvento(prev => ({ 
                          ...prev, 
                          media_promocional: url,
                          media_promocional_tipo: isVideo ? 'video' : 'image'
                        }));
                      }}
                      placeholder="https://..."
                    />
                  </div>
                  
                  {evento.media_promocional && (
                    <div className="mt-3 relative inline-block">
                      {evento.media_promocional_tipo === 'video' ? (
                        evento.media_promocional.includes('youtube') || evento.media_promocional.includes('youtu.be') ? (
                          <iframe
                            width="320"
                            height="180"
                            src={evento.media_promocional.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                            title="Video preview"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="rounded-lg"
                          />
                        ) : evento.media_promocional.startsWith('data:video') ? (
                          <video 
                            src={evento.media_promocional} 
                            controls 
                            className="max-w-xs max-h-48 rounded-lg"
                          />
                        ) : (
                          <div className="p-4 bg-gray-100 rounded-lg text-sm text-gray-600">
                            🎬 Video: {evento.media_promocional.substring(0, 50)}...
                          </div>
                        )
                      ) : (
                        <img 
                          src={evento.media_promocional} 
                          alt="Flyer preview" 
                          className="max-w-xs max-h-64 object-contain rounded-lg border bg-gray-50" 
                        />
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-1 right-1 bg-white/80 hover:bg-white"
                        onClick={() => setEvento(prev => ({ ...prev, media_promocional: '', media_promocional_tipo: '' }))}
                      >
                        ✕
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Configuración</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Requiere inicio de sesión</Label>
                    <p className="text-sm text-gray-500">Los usuarios deben estar logueados para inscribirse</p>
                  </div>
                  <Switch
                    checked={evento.requiere_login}
                    onCheckedChange={(checked) => setEvento(prev => ({ ...prev, requiere_login: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Verificar estado de deuda</Label>
                    <p className="text-sm text-gray-500">Consultar ANTUAR al momento de inscribirse</p>
                  </div>
                  <Switch
                    checked={evento.verificar_deuda}
                    onCheckedChange={(checked) => setEvento(prev => ({ ...prev, verificar_deuda: checked }))}
                  />
                </div>

                <div>
                  <Label>Modalidad de admisión</Label>
                  <Select
                    value={evento.modalidad_admision}
                    onValueChange={(value) => setEvento(prev => ({ ...prev, modalidad_admision: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmacion_email">Solo confirmación por email</SelectItem>
                      <SelectItem value="ticket_qr">Entrada con código QR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Configuración de múltiples entradas */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Permitir múltiples entradas por usuario</Label>
                      <p className="text-sm text-gray-500">Un mismo usuario puede reservar varias entradas</p>
                    </div>
                    <Switch
                      data-testid="multiple-entradas-switch"
                      checked={evento.multiple_entradas}
                      onCheckedChange={(checked) => setEvento(prev => ({ ...prev, multiple_entradas: checked }))}
                    />
                  </div>
                  
                  {evento.multiple_entradas && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <Label htmlFor="max_entradas" className="text-blue-800">Máximo de entradas por usuario</Label>
                      <p className="text-xs text-blue-600 mb-2">Límite de cuántas entradas puede reservar un único usuario</p>
                      <Input
                        id="max_entradas"
                        data-testid="max-entradas-input"
                        type="number"
                        min="1"
                        max="20"
                        value={evento.max_entradas_por_usuario}
                        onChange={(e) => setEvento(prev => ({ ...prev, max_entradas_por_usuario: parseInt(e.target.value) || 1 }))}
                        className="w-32"
                      />
                    </div>
                  )}
                </div>

                {/* Beneficio de Sello Profesional */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Sello Profesional bonificado</Label>
                      <p className="text-sm text-gray-500">
                        Los inscriptos confirmados podrán solicitar su sello en <code>/beneficio-sello</code> el día del evento.
                      </p>
                    </div>
                    <Switch
                      data-testid="beneficio-sello-switch"
                      checked={evento.tiene_beneficio_sello}
                      onCheckedChange={(checked) => setEvento(prev => ({ ...prev, tiene_beneficio_sello: checked }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Formulario */}
        <TabsContent value="formulario">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Constructor de Formulario</span>
                <Badge variant="outline">{evento.campos_formulario.length} campos</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Agregar campo */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <Label className="mb-2 block">Agregar campo:</Label>
                <div className="flex flex-wrap gap-2">
                  {TIPOS_CAMPO.map(tipo => (
                    <Button
                      key={tipo.value}
                      variant="outline"
                      size="sm"
                      onClick={() => addCampo(tipo.value)}
                    >
                      <span className="mr-1">{tipo.icon}</span>
                      {tipo.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Lista de campos */}
              {evento.campos_formulario.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay campos en el formulario</p>
                  <p className="text-sm">Agregá campos usando los botones de arriba</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {evento.campos_formulario.map((campo, index) => (
                    <div key={campo.id} className="border rounded-lg p-4 bg-white">
                      <div className="flex items-start gap-4">
                        {/* Controles de orden */}
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveCampo(index, -1)}
                            disabled={index === 0}
                            className="h-6 w-6 p-0"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <div className="flex items-center justify-center h-6 w-6">
                            <GripVertical className="w-4 h-4 text-gray-400" />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveCampo(index, 1)}
                            disabled={index === evento.campos_formulario.length - 1}
                            className="h-6 w-6 p-0"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Configuración del campo */}
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="secondary">
                              {TIPOS_CAMPO.find(t => t.value === campo.tipo)?.icon} {TIPOS_CAMPO.find(t => t.value === campo.tipo)?.label}
                            </Badge>
                            <span className="text-xs text-gray-400">ID: {campo.id}</span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Etiqueta</Label>
                              <Input
                                value={campo.label}
                                onChange={(e) => updateCampo(index, 'label', e.target.value)}
                                placeholder="Nombre del campo"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Placeholder</Label>
                              <Input
                                value={campo.placeholder || ''}
                                onChange={(e) => updateCampo(index, 'placeholder', e.target.value)}
                                placeholder="Texto de ayuda..."
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Prellenar con dato del usuario</Label>
                              <Select
                                value={campo.prellenar_con || 'none'}
                                onValueChange={(value) => updateCampo(index, 'prellenar_con', value === 'none' ? '' : value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Sin prellenar" />
                                </SelectTrigger>
                                <SelectContent>
                                  {CAMPOS_PRELLENADO.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Sección (agrupador)</Label>
                              <Input
                                value={campo.seccion || ''}
                                onChange={(e) => updateCampo(index, 'seccion', e.target.value)}
                                placeholder="Ej: Datos personales"
                              />
                            </div>
                          </div>

                          {/* Opciones para campos de selección */}
                          {['select', 'multiselect', 'dropdown'].includes(campo.tipo) && (
                            <div>
                              <Label className="text-xs">Opciones (una por línea)</Label>
                              <Textarea
                                value={(campo.options || []).join('\n')}
                                onChange={(e) => updateCampo(index, 'options', e.target.value.split('\n'))}
                                onBlur={(e) => updateCampo(index, 'options', e.target.value.split('\n').filter(o => o.trim()))}
                                placeholder={"Opción 1\nOpción 2\nOpción 3"}
                                rows={4}
                              />
                              <p className="text-xs text-gray-400 mt-1">Presioná Enter para agregar más opciones</p>
                            </div>
                          )}

                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={campo.required}
                                onChange={(e) => updateCampo(index, 'required', e.target.checked)}
                                className="rounded"
                              />
                              <span className="text-sm">Obligatorio</span>
                            </label>
                          </div>
                        </div>

                        {/* Acciones */}
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => duplicarCampo(index)}
                            className="h-8 w-8 p-0"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCampo(index)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Pago */}
        <TabsContent value="pago">
          <Card>
            <CardHeader>
              <CardTitle>Configuración de Pago</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Tipo de pago</Label>
                <Select
                  value={evento.configuracion_pago.tipo}
                  onValueChange={(value) => setEvento(prev => ({
                    ...prev,
                    configuracion_pago: { ...prev.configuracion_pago, tipo: value }
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gratis">Gratis</SelectItem>
                    <SelectItem value="pago_fijo">Pago fijo</SelectItem>
                    <SelectItem value="condicional">Condicional (según estado de deuda)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {evento.configuracion_pago.tipo !== 'gratis' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="precio">
                        {evento.configuracion_pago.tipo === 'condicional' ? 'Precio (matriculado al día)' : 'Precio'}
                      </Label>
                      <Input
                        id="precio"
                        type="number"
                        value={evento.configuracion_pago.precio}
                        onChange={(e) => setEvento(prev => ({
                          ...prev,
                          configuracion_pago: { ...prev.configuracion_pago, precio: e.target.value }
                        }))}
                        placeholder="0"
                      />
                    </div>

                    {evento.configuracion_pago.tipo === 'condicional' && (
                      <div>
                        <Label htmlFor="precio_con_deuda">Precio (con deuda)</Label>
                        <Input
                          id="precio_con_deuda"
                          type="number"
                          value={evento.configuracion_pago.precio_con_deuda}
                          onChange={(e) => setEvento(prev => ({
                            ...prev,
                            configuracion_pago: { ...prev.configuracion_pago, precio_con_deuda: e.target.value }
                          }))}
                          placeholder="0"
                        />
                      </div>
                    )}
                  </div>

                  {evento.configuracion_pago.tipo === 'condicional' && (
                    <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                      <div>
                        <Label>Permitir inscripción con deuda</Label>
                        <p className="text-sm text-gray-500">Si está desactivado, solo podrán inscribirse los matriculados al día</p>
                      </div>
                      <Switch
                        checked={evento.configuracion_pago.permitir_con_deuda}
                        onCheckedChange={(checked) => setEvento(prev => ({
                          ...prev,
                          configuracion_pago: { ...prev.configuracion_pago, permitir_con_deuda: checked }
                        }))}
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="datos_transferencia">Datos para transferencia/pago</Label>
                    <Textarea
                      id="datos_transferencia"
                      value={evento.configuracion_pago.datos_transferencia || ''}
                      onChange={(e) => setEvento(prev => ({
                        ...prev,
                        configuracion_pago: { ...prev.configuracion_pago, datos_transferencia: e.target.value }
                      }))}
                      placeholder="CBU: ...&#10;Alias: ...&#10;Titular: ..."
                      rows={4}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Ticket/QR */}
        <TabsContent value="ticket">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                Configuración de Ticket/Entrada
              </CardTitle>
            </CardHeader>
            <CardContent>
              {evento.modalidad_admision !== 'ticket_qr' ? (
                <div className="text-center py-8 text-gray-500">
                  <QrCode className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Esta configuración solo aplica cuando la modalidad es "Entrada con código QR"</p>
                  <p className="text-sm mt-2">Podés cambiarla en la pestaña "General"</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="p-4 bg-violet-50 rounded-lg">
                    <p className="text-violet-800">
                      Cargá una imagen de fondo para el ticket. Los datos del inscripto y el código QR se superpondrán sobre esta imagen.
                    </p>
                  </div>

                  <div>
                    <Label>Template de ticket (imagen de fondo)</Label>
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setEvento(prev => ({
                                ...prev,
                                configuracion_ticket: { ...prev.configuracion_ticket, template_base64: reader.result }
                              }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      <div className="text-xs text-gray-500">O ingresá una URL:</div>
                      <Input
                        value={evento.configuracion_ticket?.template_base64?.startsWith('data:') ? '' : (evento.configuracion_ticket?.template_base64 || '')}
                        onChange={(e) => setEvento(prev => ({
                          ...prev,
                          configuracion_ticket: { ...prev.configuracion_ticket, template_base64: e.target.value }
                        }))}
                        placeholder="https://..."
                      />
                    </div>
                    {evento.configuracion_ticket?.template_base64 && (
                      <div className="mt-3 relative inline-block">
                        <img 
                          src={evento.configuracion_ticket.template_base64} 
                          alt="Preview ticket" 
                          className="max-h-48 object-contain rounded-lg border"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute top-1 right-1 bg-white/80 hover:bg-white"
                          onClick={() => setEvento(prev => ({
                            ...prev,
                            configuracion_ticket: { ...prev.configuracion_ticket, template_base64: '' }
                          }))}
                        >
                          ✕
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Ancho (px)</Label>
                      <Input
                        type="number"
                        value={evento.configuracion_ticket?.ancho || 800}
                        onChange={(e) => setEvento(prev => ({
                          ...prev,
                          configuracion_ticket: { ...prev.configuracion_ticket, ancho: parseInt(e.target.value) }
                        }))}
                      />
                    </div>
                    <div>
                      <Label>Alto (px)</Label>
                      <Input
                        type="number"
                        value={evento.configuracion_ticket?.alto || 400}
                        onChange={(e) => setEvento(prev => ({
                          ...prev,
                          configuracion_ticket: { ...prev.configuracion_ticket, alto: parseInt(e.target.value) }
                        }))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
